import React from 'react';
import PropTypes from 'prop-types';
import { Card as MuiCard, Box, Typography, useTheme } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * KPI metric tile with colourful side border, icon and percentage variation.
 * Pure MUI implementation so it adapts automatically to the current theme (light/dark).
 *
 * @param {object} props
 * @param {string} props.title  Metric title displayed in uppercase caption.
 * @param {number|string} props.value  Numeric or string value to show.
 * @param {string} [props.icon]  Bootstrap icon name or any custom JSX – rendered inside a 48×48 bubble.
 * @param {string} [props.color]  Hex colour used for side-border and icon bubble. Falls back to theme primary colour.
 * @param {number} [props.change]  Percentage variation. Positive → green arrow up, negative → red arrow down.
 */
export default function MetricCard ({ title, value, icon, color, change }) {
  const theme = useTheme();
  const paletteColor = color || theme.palette.primary.main;
  const isPositive = typeof change === 'number' ? change >= 0 : null;

  return (
    <MuiCard
      elevation={0}
      sx={{
        position: 'relative',
        p: 2,
        border: '1px solid #eaeaea',
        borderRadius: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          width: 4,
          height: '100%',
          borderTopLeftRadius: 2,
          borderBottomLeftRadius: 2,
          backgroundColor: paletteColor,
        },
      }}
    >
      {/* Metric text */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          
          {typeof value === 'number' && !Number.isNaN(value)
            ? value.toLocaleString('en', { maximumFractionDigits: 0 })
            : value}
        </Typography>
        {typeof change === 'number' ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            {isPositive ? (
              <ArrowUpwardIcon fontSize="inherit" color="success" />
            ) : (
              <ArrowDownwardIcon fontSize="inherit" color="error" />
            )}
            <Typography variant="body2" color={isPositive ? 'success.main' : 'error.main'}>
              {Math.abs(change).toFixed(1)}%
            </Typography>
          </Box>
        ) : change === null ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Datos insuficientes
          </Typography>
        ) : null}
      </Box>

      {/* Icon bubble */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: `${paletteColor}15`, // ~8% opacity overlay
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* If a string is provided assume bootstrap-icon className else render JSX */}
        {typeof icon === 'string' ? (
          <i
            className={`bi bi-${icon}`}
            style={{ color: paletteColor, fontSize: '1.25rem' }}
            aria-label={title}
          />
        ) : (
          React.cloneElement(icon, {
            style: { color: paletteColor },
            'aria-label': title,
          })
        )}
      </Box>
    </MuiCard>
  );
}

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  color: PropTypes.string,
  change: PropTypes.number,
};
